import { GameState, GameStats } from './types';


export class StorageService {
  private readonly KEYS = {
    GAME_STATS: 'quebecle_stats',
    LAST_PLAYED: 'quebecle_last_played',
    CURRENT_GAME: 'quebecle_current_game'
  } as const;
  
  // Initialize storage with default values if not exists
  public init(): void {
    if (!this.getStats()) {
      this.saveStats(this.getDefaultStats());
    }
  }
  
  // Get default statistics structure
  private getDefaultStats(): GameStats {
    return {
      gamesPlayed: 0,
      gamesWon: 0,
      currentStreak: 0,
      maxStreak: 0,
      guessDistribution: {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0
      },
      lastPlayed: null
    };
  }
  
  public getStats(): GameStats | null {
    try {
      const stats = localStorage.getItem(this.KEYS.GAME_STATS);
      return stats ? JSON.parse(stats) : null;
    } catch (error) {
      console.error('Error loading stats from localStorage:', error);
      return null;
    }
  }
  
  public saveStats(stats: GameStats): void {
    try {
      localStorage.setItem(this.KEYS.GAME_STATS, JSON.stringify(stats));
    } catch (error) {
      console.error('Error saving stats to localStorage:', error);
    }
  }
  
  public updateStats(won: boolean, attempts: number): void {
    const stats = this.getStats() || this.getDefaultStats();
    
    stats.gamesPlayed++;
    stats.lastPlayed = new Date().toISOString();
    
    if (won) {
      stats.gamesWon++;
      stats.currentStreak++;
      stats.maxStreak = Math.max(stats.maxStreak, stats.currentStreak);
      
      // Update guess distribution
      if (attempts >= 1 && attempts <= 5) {
        stats.guessDistribution[attempts as keyof typeof stats.guessDistribution]++;
      }
    } else {
      stats.currentStreak = 0;
    }
    
    this.saveStats(stats);
  }
  

  public getWinPercentage(): number {
    const stats = this.getStats();
    if (!stats || stats.gamesPlayed === 0) return 0;
    
    return Math.round((stats.gamesWon / stats.gamesPlayed) * 100);
  }
  
  public getCurrentGame(): GameState | null {
    try {
      const game = localStorage.getItem(this.KEYS.CURRENT_GAME);
      return game ? JSON.parse(game) : null;
    } catch (error) {
      console.error('Error loading current game from localStorage:', error);
      return null;
    }
  }
  
  public saveCurrentGame(gameState: GameState): void {
    try {
      localStorage.setItem(this.KEYS.CURRENT_GAME, JSON.stringify(gameState));
    } catch (error) {
      console.error('Error saving current game to localStorage:', error);
    }
  }
  
  public clearCurrentGame(): void {
    try {
      localStorage.removeItem(this.KEYS.CURRENT_GAME);
    } catch (error) {
      console.error('Error clearing current game from localStorage:', error);
    }
  }
  
  public hasPlayedToday(): boolean {
    const stats = this.getStats();
    if (!stats || !stats.lastPlayed) return false;
    
    const lastPlayed = new Date(stats.lastPlayed);
    const today = new Date();
    
    return lastPlayed.toDateString() === today.toDateString();
  }
  
  public getLastPlayedDate(): Date | null {
    const stats = this.getStats();
    if (!stats || !stats.lastPlayed) return null;
    
    return new Date(stats.lastPlayed);
  }
  
  public exportData(): { stats: GameStats | null; currentGame: GameState | null; exportDate: string } {
    return {
      stats: this.getStats(),
      currentGame: this.getCurrentGame(),
      exportDate: new Date().toISOString()
    };
  }
  
  public importData(data: { stats?: GameStats; currentGame?: GameState }): boolean {
    try {
      if (data.stats) {
        this.saveStats(data.stats);
      }
      if (data.currentGame) {
        this.saveCurrentGame(data.currentGame);
      }
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  }
  
  public clearAllData(): void {
    try {
      Object.values(this.KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing data:', error);
    }
  }
  
  public isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }
  
  public getStorageInfo(): { 
    available: boolean; 
    totalSize?: number; 
    quebecleSize?: number; 
    totalSizeFormatted?: string; 
    quebeecleSizeFormatted?: string; 
    error?: string; 
  } {
    if (!this.isStorageAvailable()) {
      return { available: false };
    }
    
    let totalSize = 0;
    let quebecleSize = 0;
    
    try {
      // Calculate total localStorage usage
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          totalSize += localStorage[key].length;
          if (key.startsWith('quebecle_')) {
            quebecleSize += localStorage[key].length;
          }
        }
      }
      
      return {
        available: true,
        totalSize: totalSize,
        quebecleSize: quebecleSize,
        totalSizeFormatted: this.formatBytes(totalSize),
        quebeecleSizeFormatted: this.formatBytes(quebecleSize)
      };
    } catch (error) {
      return { available: true, error: (error as Error).message };
    }
  }
  
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
